import { useEffect, useRef } from 'react';
import { RootState } from 'store';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

/**
 * Cookieless page-view counter (backend POST /api/v1/pv → Supabase page_views_daily).
 * Sends only a page key, the sign-in state (signed out, signed in without profile, role) and
 * whether it is the first page of this load of the site. No id, nothing stored on the device.
 */

const API_BASE = process.env.REACT_APP_API_BASE_URL;
/** Time a page must stay displayed to count: skips the guards' instant redirects. */
const SETTLE_MS = 800;

const PAGES: [RegExp, string][] = [
  [/^$/, 'home'],
  [/^login$/, 'login'],
  [/^dashboard$/, 'dashboard'],
  [/^complete-profile$/, 'complete_profile'],
  [/^accommodations$/, 'accommodations_search'],
  [/^accommodations\/[^/]+$/, 'accommodation_detail'],
  [/^roommates$/, 'roommates_search'],
  [/^roommates\/[^/]+$/, 'roommate_detail'],
  [/^jobs(\/.*)?$/, 'jobs'],
  [/^mentors$/, 'mentors'],
  [/^messages(\/.*)?$/, 'messages'],
  [/^profile$/, 'profile'],
  [/^accommodation-create$/, 'accommodation_create'],
  [/^become-host$/, 'become_host'],
  [/^my-job-offers$/, 'job_offers'],
  [/^employers$/, 'employers'],
  [/^info-accommodations$/, 'info_accommodations'],
  [/^info-roommates$/, 'info_roommates'],
  [/^free-housing-for-refugees$/, 'free_housing'],
  [/^coliving-for-refugees$/, 'coliving'],
  [/^(remote-jobs-for-ukrainians|jobs-articles\/.*)$/, 'jobs_articles'],
  [/^donations$/, 'donations'],
  [/^(faq|safety-tips)$/, 'faq'],
  [/^contact-us$/, 'contact'],
  [/^(about-us|press|useful-links|stories\/.*)$/, 'about'],
];

export function pageKey(pathname: string): string {
  const rest = pathname.replace(/^\/[a-z]{2}(\/|$)/, '').replace(/\/+$/, '');
  const found = PAGES.find(([re]) => re.test(rest));
  return found ? found[1] : 'other';
}

let firstSent = false;

function send(page: string, state: string) {
  if (!API_BASE) return;
  const url = `${API_BASE}/api/v1/pv?p=${page}&s=${state}${firstSent ? '' : '&e=1'}`;
  firstSent = true;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url)) return;
    fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors' }).catch(() => undefined);
  } catch {
    // counting must never break the page
  }
}

function PageViewTracker(): null {
  const { pathname } = useLocation();
  const authLoading = useSelector((s: RootState) => s.auth.loading);
  const signedIn = useSelector((s: RootState) => !!s.auth.user);
  const { data, status, profileMissing } = useSelector((s: RootState) => s.user);
  const lastSent = useRef<string | null>(null);

  let state: string | null = null;
  if (!authLoading) {
    if (!signedIn) state = 'anon';
    else if (profileMissing) state = 'no_profile';
    else if (data?.role) state = data.role;
    else if (status === 'failed') state = 'unknown';
  }

  useEffect(() => {
    if (!state || lastSent.current === pathname) return undefined;
    const timer = window.setTimeout(() => {
      lastSent.current = pathname;
      send(pageKey(pathname), state as string);
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [pathname, state]);

  return null;
}

export default PageViewTracker;
